import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  BookOpen, PlayCircle, Loader2, Plus, Trash2, LogOut, ClipboardList,
  LayoutDashboard, Library, GraduationCap, Search, RefreshCw, Video,
  AlertCircle, ChevronRight, ChevronDown, User, Save, Eye, Folder,
  FileText, FolderOpen, Sparkles, Hash
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const toEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  if (url.match(/\.(mp4|webm|ogg)(\?|$)/i)) return url;
  return null;
};

const ContentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('dashboard');
  const [courses, setCourses] = useState([]);
  const [allCourseDetails, setAllCourseDetails] = useState({});  // cid -> {..., modules:[], assignments:[]}
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});   // module_id -> bool
  const [activeTopic, setActiveTopic] = useState(null);     // {course_id, module_id}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Editor state
  const [form, setForm] = useState({
    title: '', description: '', video_url: '', content_markdown: '',
  });
  const [previewMd, setPreviewMd] = useState(false);

  // Topic's assignments (inline CRUD, no pop-up)
  const [newAssignment, setNewAssignment] = useState({ title: '', description: '', due_date: '', max_points: 100 });
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content/my-courses`, { headers: authHdr() });
      setCourses(res.data || []);
    } catch { toast.error('Failed to load courses'); }
    setLoading(false);
  }, []);

  const fetchAllDetails = useCallback(async () => {
    if (courses.length === 0) return;
    const map = {};
    await Promise.all(courses.map(async (c) => {
      try {
        const [cRes, aRes] = await Promise.all([
          axios.get(`${API}/content/courses/${c.course_id}`, { headers: authHdr() }),
          axios.get(`${API}/content/courses/${c.course_id}/assignments`, { headers: authHdr() }),
        ]);
        map[c.course_id] = { ...cRes.data, assignments: aRes.data || [] };
      } catch { /* ignore */ }
    }));
    setAllCourseDetails(map);
  }, [courses]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchAllDetails(); }, [fetchAllDetails]);

  useEffect(() => {
    if (courses.length > 0 && Object.keys(expandedCourses).length === 0) {
      setExpandedCourses({ [courses[0].course_id]: true });
    }
  }, [courses, expandedCourses]);

  // Load active topic into form
  useEffect(() => {
    if (!activeTopic) return;
    const c = allCourseDetails[activeTopic.course_id];
    if (!c) return;
    const m = (c.modules || []).find(x => x.module_id === activeTopic.module_id);
    if (!m) return;
    setForm({
      title: m.title || '',
      description: m.description || '',
      video_url: m.video_url || '',
      content_markdown: m.content_markdown || '',
    });
    setEditingAssignmentId(null);
    setNewAssignment({ title: '', description: '', due_date: '', max_points: 100 });
  }, [activeTopic, allCourseDetails]);

  const refreshAll = async () => {
    setRefreshing(true);
    await fetchCourses();
    await fetchAllDetails();
    setRefreshing(false);
    toast.success('Course Published');
  };

  // Build tree from flat modules: returns {rootNodes, childrenMap}
  const buildTree = (modules) => {
    const map = {};
    modules.forEach(m => { map[m.module_id] = { ...m, children: [] }; });
    const roots = [];
    modules.forEach(m => {
      if (m.parent_id && map[m.parent_id]) {
        map[m.parent_id].children.push(map[m.module_id]);
      } else {
        roots.push(map[m.module_id]);
      }
    });
    const sortRec = (nodes) => {
      nodes.sort((a, b) => (a.order || 0) - (b.order || 0));
      nodes.forEach(n => sortRec(n.children));
    };
    sortRec(roots);
    return roots;
  };

  const seedBdesYear1 = async (courseId) => {
    if (!window.confirm('Seed the full B.Des Animation Year 1 curriculum? This will add ~90 topics in a tree structure.')) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/content/courses/${courseId}/seed-bdes-year1`, {}, { headers: authHdr() });
      toast.success(res.data.skipped ? 'Already seeded' : `${res.data.modules_added} topics added`);
      await fetchAllDetails();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Seed failed'); }
    setSeeding(false);
  };

  // ===== Topic editor actions =====
  const saveTopic = async () => {
    if (!activeTopic) return;
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const c = allCourseDetails[activeTopic.course_id];
      const m = (c.modules || []).find(x => x.module_id === activeTopic.module_id);
      await axios.put(`${API}/content/courses/${activeTopic.course_id}/modules/${activeTopic.module_id}`, {
        ...m,                       // preserve existing fields like parent_id, type, order
        title: form.title,
        description: form.description,
        video_url: form.video_url,
        content_markdown: form.content_markdown,
      }, { headers: authHdr() });
      toast.success('Topic saved');
      await fetchAllDetails();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const deleteTopic = async () => {
    if (!activeTopic) return;
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/content/courses/${activeTopic.course_id}/modules/${activeTopic.module_id}`,
        { headers: authHdr() });
      toast.success('Deleted');
      setActiveTopic(null);
      await fetchAllDetails();
    } catch { toast.error('Delete failed'); }
  };

  // ===== Add child module under a folder (folder or topic) =====
  const addChild = async (courseId, parentId, parentType, title, type) => {
    const childType = type || 'topic';
    if (!title || !title.trim()) return null;
    try {
      const res = await axios.post(`${API}/content/courses/${courseId}/modules`, {
        title: title.trim(),
        parent_id: parentId,
        type: childType,
        order: 999,
      }, { headers: authHdr() });
      toast.success(`${childType === 'folder' ? 'Section' : 'Topic'} added`);
      await fetchAllDetails();
      if (parentId) setExpandedNodes(p => ({ ...p, [parentId]: true }));
      if (childType === 'topic') setActiveTopic({ course_id: courseId, module_id: res.data.module_id });
      return res.data.module_id;
    } catch (err) { toast.error(err?.response?.data?.detail || 'Add failed'); }
    return null;
  };

  // ===== Assignment CRUD (per topic, inline) =====
  const topicAssignments = useMemo(() => {
    if (!activeTopic) return [];
    const c = allCourseDetails[activeTopic.course_id];
    if (!c) return [];
    return (c.assignments || []).filter(a => a.module_id === activeTopic.module_id);
  }, [activeTopic, allCourseDetails]);

  const saveNewAssignment = async () => {
    if (!activeTopic) return;
    if (!newAssignment.title.trim() || !newAssignment.description.trim()) {
      toast.error('Title + description required'); return;
    }
    try {
      await axios.post(`${API}/content/courses/${activeTopic.course_id}/assignments`, {
        ...newAssignment, module_id: activeTopic.module_id,
      }, { headers: authHdr() });
      toast.success('Assignment created');
      setNewAssignment({ title: '', description: '', due_date: '', max_points: 100 });
      await fetchAllDetails();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Create failed'); }
  };

  const updateInlineAssignment = async (a, patch) => {
    try {
      await axios.put(`${API}/content/courses/${activeTopic.course_id}/assignments/${a.assignment_id}`, {
        ...a, ...patch,
      }, { headers: authHdr() });
      toast.success('Saved');
      setEditingAssignmentId(null);
      await fetchAllDetails();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const deleteInlineAssignment = async (aid) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await axios.delete(`${API}/content/courses/${activeTopic.course_id}/assignments/${aid}`, { headers: authHdr() });
      toast.success('Deleted');
      await fetchAllDetails();
    } catch { toast.error('Delete failed'); }
  };

  // ===== Derived stats =====
  const stats = useMemo(() => {
    const allTopics = Object.values(allCourseDetails).flatMap(c =>
      (c.modules || []).filter(m => m.type !== 'folder'));
    const allAssignments = Object.values(allCourseDetails).flatMap(c => c.assignments || []);
    const videosCount = allTopics.filter(m => m.video_url).length;
    const emptyTopics = allTopics.filter(m => !m.video_url && !m.content_markdown).length;
    return {
      courses: courses.length,
      topics: allTopics.length,
      assignments: allAssignments.length,
      videos: videosCount,
      emptyTopics,
    };
  }, [courses, allCourseDetails]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  // =====  TREE NODE (recursive) =====
  const TreeNode = ({ node, courseId, depth }) => {
    const isFolder = node.type === 'folder' || (node.children && node.children.length > 0);
    const isActive = activeTopic?.course_id === courseId && activeTopic?.module_id === node.module_id;
    const expanded = !!expandedNodes[node.module_id];
    const [adding, setAdding] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newKind, setNewKind] = useState('topic');

    const handleClick = () => {
      if (isFolder) setExpandedNodes(p => ({ ...p, [node.module_id]: !expanded }));
      else setActiveTopic({ course_id: courseId, module_id: node.module_id });
    };

    const submitAdd = async () => {
      if (!newTitle.trim()) return;
      await addChild(courseId, node.module_id, node.type, newTitle, newKind);
      setNewTitle(''); setAdding(false);
    };

    return (
      <div>
        <div
          className={`flex items-center gap-1.5 py-1 pr-1 rounded-md text-[13px] group transition-colors cursor-pointer ${
            isActive
              ? 'bg-emerald-500/15 text-emerald-400 font-medium'
              : isFolder
                ? 'text-foreground/85 hover:bg-accent'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
          style={{ paddingLeft: `${depth * 14 + 6}px` }}
          data-testid={`tree-node-${node.module_id}`}
        >
          <button onClick={handleClick} className="flex items-center gap-1.5 flex-1 min-w-0">
            {isFolder ? (
              expanded
                ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            ) : <span className="w-3.5 shrink-0" />}
            {isFolder
              ? (expanded ? <FolderOpen className="w-3.5 h-3.5 shrink-0 text-amber-400/80" /> : <Folder className="w-3.5 h-3.5 shrink-0 text-amber-400/70" />)
              : <FileText className="w-3.5 h-3.5 shrink-0 text-sky-400/70" />
            }
            <span className="flex-1 text-left truncate">{node.title}</span>
            {!isFolder && !node.video_url && !node.content_markdown && (
              <AlertCircle className="w-3 h-3 text-rose-400/70 shrink-0" title="No content yet" />
            )}
          </button>
          {isFolder && (
            <button
              onClick={(e) => { e.stopPropagation(); setAdding(true); setExpandedNodes(p => ({ ...p, [node.module_id]: true })); }}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-emerald-500/20 text-emerald-400 transition"
              title="Add child"
              data-testid={`tree-add-${node.module_id}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
        {isFolder && expanded && (
          <div>
            {(node.children || []).map(child => (
              <TreeNode key={child.module_id} node={child} courseId={courseId} depth={depth + 1} />
            ))}
            {adding && (
              <div className="flex items-center gap-1 py-1 pr-1" style={{ paddingLeft: `${(depth + 1) * 14 + 6}px` }}>
                <select value={newKind} onChange={e => setNewKind(e.target.value)}
                  className="bg-background border border-border rounded px-1 py-0.5 text-[11px]">
                  <option value="topic">Topic</option>
                  <option value="folder">Chapter</option>
                </select>
                <Input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitAdd(); if (e.key === 'Escape') setAdding(false); }}
                  placeholder={newKind === 'folder' ? 'New chapter...' : 'New topic...'}
                  className="h-6 text-[11px] flex-1" />
                <button onClick={submitAdd} className="text-emerald-400 text-[11px] px-1">✓</button>
                <button onClick={() => setAdding(false)} className="text-muted-foreground text-[11px] px-1">✕</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ===== SIDEBAR =====
  const Sidebar = () => {
    const toggleCourse = (cid) => setExpandedCourses(p => ({ ...p, [cid]: !p[cid] }));
    return (
      <aside className="w-80 border-r border-border bg-card/30 backdrop-blur-sm flex flex-col shrink-0 h-screen sticky top-0">
        <div className="p-5 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-base leading-tight">Content Studio</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">VIA · Curriculum</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          <div className="px-3 space-y-0.5">
            <NavItem icon={LayoutDashboard} label="Dashboard" active={view === 'dashboard' && !activeTopic}
              onClick={() => { setView('dashboard'); setActiveTopic(null); }} testId="nav-dashboard" />
            <NavItem icon={User} label="Profile" active={view === 'profile'}
              onClick={() => { setView('profile'); setActiveTopic(null); }} testId="nav-profile" />
          </div>

          <div className="mt-5 px-3">
            <div className="flex items-center justify-between mb-2 px-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Curriculum</div>
              <Badge variant="outline" className="text-[10px] h-5">{courses.length}</Badge>
            </div>
            <div className="space-y-0.5">
              {courses.map(c => {
                const detail = allCourseDetails[c.course_id] || {};
                const mods = detail.modules || [];
                const tree = buildTree(mods);
                const exp = !!expandedCourses[c.course_id];
                return (
                  <div key={c.course_id} className="mb-2">
                    <button
                      onClick={() => toggleCourse(c.course_id)}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm font-medium text-foreground/90 hover:bg-accent transition-all"
                      data-testid={`course-tree-${c.course_id}`}
                    >
                      {exp ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      <BookOpen className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="flex-1 text-left truncate">{c.title}</span>
                      <Badge variant="outline" className="text-[9px]">{mods.filter(m => m.type !== 'folder').length}</Badge>
                    </button>
                    {exp && (
                      <div className="mt-1">
                        {tree.length === 0 ? (
                          <div className="py-4 px-3 text-center">
                            <div className="text-[11px] text-muted-foreground mb-2">No curriculum yet</div>
                            <Button size="sm" variant="outline"
                              onClick={() => seedBdesYear1(c.course_id)} disabled={seeding}
                              className="h-7 text-[11px] gap-1.5"
                              data-testid={`seed-btn-${c.course_id}`}>
                              {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                              Seed B.Des Year 1
                            </Button>
                          </div>
                        ) : (
                          <>
                            {tree.map(node => (
                              <TreeNode key={node.module_id} node={node} courseId={c.course_id} depth={0} />
                            ))}
                            <button
                              onClick={async () => {
                                const title = window.prompt('New top-level section (e.g. "Year 2"):');
                                if (title) await addChild(c.course_id, null, null, title, 'folder');
                              }}
                              className="w-full mt-1 flex items-center gap-1.5 px-2 py-1.5 rounded text-[11px] text-muted-foreground hover:bg-accent hover:text-emerald-400 transition"
                              data-testid={`add-root-${c.course_id}`}
                            >
                              <Plus className="w-3 h-3" /> Add top-level section
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="p-3 border-t border-border space-y-2 shrink-0">
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}
            className="w-full justify-start" data-testid="publish-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Publish Course
          </Button>
          <div className="rounded-lg bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground">Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <Badge variant="outline" className="text-[9px] mt-1">Content Executive</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }}
            className="w-full justify-start text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
            data-testid="ce-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>
    );
  };

  const NavItem = ({ icon: Icon, label, active, onClick, testId }) => (
    <button onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
        active ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/10 text-emerald-400 font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      }`}>
      <Icon className="w-4 h-4" />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );

  // ===== TOPIC EDITOR =====
  const TopicEditor = () => {
    const embed = toEmbedUrl(form.video_url);
    const courseTitle = (allCourseDetails[activeTopic?.course_id] || {}).title || '';
    // Build breadcrumb from module tree
    const breadcrumb = useMemo(() => {
      const c = allCourseDetails[activeTopic?.course_id];
      if (!c) return [];
      const byId = {};
      (c.modules || []).forEach(m => { byId[m.module_id] = m; });
      const path = [];
      let cur = byId[activeTopic?.module_id];
      while (cur) {
        path.unshift(cur.title);
        cur = cur.parent_id ? byId[cur.parent_id] : null;
      }
      return path;
    }, [activeTopic, allCourseDetails]);

    return (
      <div className="max-w-4xl mx-auto px-8 py-8" data-testid="topic-editor">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted-foreground mb-1 truncate">
              {courseTitle} · {breadcrumb.slice(0, -1).join(' › ') || 'Topic'}
            </div>
            <h1 className="text-xl font-semibold truncate">{form.title || 'Topic'}</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={deleteTopic} className="text-rose-400 hover:bg-rose-500/10" data-testid="topic-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
            <Button onClick={saveTopic} disabled={saving} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="topic-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </div>
        </div>

        <input
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          placeholder="Topic title..."
          className="w-full text-3xl font-bold bg-transparent border-0 outline-none focus:ring-0 placeholder:text-muted-foreground/40 mb-3"
          data-testid="topic-title"
        />

        <textarea
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="Short description (1-2 sentences shown on the topic card)..."
          rows={2}
          className="w-full text-base text-muted-foreground bg-transparent border-0 outline-none focus:ring-0 resize-none placeholder:text-muted-foreground/40 mb-6"
          data-testid="topic-description"
        />

        {/* ========= VIDEO SECTION ========= */}
        <Section icon={Video} title="Video" subtitle="YouTube, Vimeo, or direct MP4 link">
          <Input
            value={form.video_url}
            onChange={e => setForm({ ...form, video_url: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
            className="font-mono text-sm"
            data-testid="topic-video-url"
          />
          {form.video_url && (
            embed ? (
              <div className="mt-3 rounded-xl overflow-hidden border border-border bg-black aspect-video">
                {embed.match(/\.(mp4|webm|ogg)(\?|$)/i) ? (
                  <video src={embed} controls className="w-full h-full" />
                ) : (
                  <iframe src={embed} className="w-full h-full" allowFullScreen title="Video preview" />
                )}
              </div>
            ) : (
              <div className="mt-3 p-4 rounded-lg bg-rose-500/10 border border-rose-500/20 text-sm text-rose-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Unsupported URL. Use YouTube, Vimeo, or a direct .mp4/.webm link.
              </div>
            )
          )}
        </Section>

        {/* ========= READING MATERIAL (optional) ========= */}
        <Section
          icon={FileText}
          title="Reading Material"
          subtitle="Optional — supports markdown (# headings, **bold**, lists, images)"
          right={
            <div className="flex gap-1 rounded-lg border border-border p-0.5 bg-muted/30">
              <button onClick={() => setPreviewMd(false)}
                className={`px-2.5 py-1 text-xs rounded ${!previewMd ? 'bg-background text-foreground' : 'text-muted-foreground'}`}>Edit</button>
              <button onClick={() => setPreviewMd(true)}
                className={`px-2.5 py-1 text-xs rounded flex items-center gap-1 ${previewMd ? 'bg-background text-foreground' : 'text-muted-foreground'}`}>
                <Eye className="w-3 h-3" /> Preview
              </button>
            </div>
          }
        >
          {previewMd ? (
            <div className="min-h-[160px] p-5 rounded-lg bg-muted/20 border border-border prose prose-invert prose-sm max-w-none prose-img:rounded-lg prose-a:text-emerald-400 prose-headings:text-foreground prose-strong:text-foreground">
              {form.content_markdown
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content_markdown}</ReactMarkdown>
                : <p className="text-muted-foreground italic">Nothing to preview.</p>
              }
            </div>
          ) : (
            <Textarea
              value={form.content_markdown}
              onChange={e => setForm({ ...form, content_markdown: e.target.value })}
              placeholder="# Learning objectives&#10;- Objective 1&#10;- Objective 2&#10;&#10;## Key concepts&#10;..."
              rows={10}
              className="font-mono text-sm leading-relaxed"
              data-testid="topic-markdown"
            />
          )}
        </Section>

        {/* ========= ASSIGNMENTS SECTION ========= */}
        <Section icon={ClipboardList} title="Assignments" subtitle={`${topicAssignments.length} assignment${topicAssignments.length === 1 ? '' : 's'} for this topic`}>
          <div className="space-y-3">
            {topicAssignments.map(a => (
              <AssignmentRow key={a.assignment_id} a={a}
                isEditing={editingAssignmentId === a.assignment_id}
                onStartEdit={() => setEditingAssignmentId(a.assignment_id)}
                onCancelEdit={() => setEditingAssignmentId(null)}
                onSave={(patch) => updateInlineAssignment(a, patch)}
                onDelete={() => deleteInlineAssignment(a.assignment_id)}
              />
            ))}

            {/* Inline "add new" form (always visible) */}
            <div className="rounded-xl border border-dashed border-border p-4 bg-muted/20">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-4 h-4 text-violet-400" />
                <div className="text-sm font-medium">Create new assignment</div>
              </div>
              <div className="space-y-2">
                <Input placeholder="Assignment title (e.g. Sketch 3 line studies)"
                  value={newAssignment.title}
                  onChange={e => setNewAssignment({ ...newAssignment, title: e.target.value })}
                  data-testid="new-assignment-title" />
                <Textarea placeholder="Description — what should the student do?"
                  rows={2}
                  value={newAssignment.description}
                  onChange={e => setNewAssignment({ ...newAssignment, description: e.target.value })}
                  data-testid="new-assignment-desc" />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={newAssignment.due_date}
                    onChange={e => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                    placeholder="Due date" />
                  <Input type="number" value={newAssignment.max_points}
                    onChange={e => setNewAssignment({ ...newAssignment, max_points: parseInt(e.target.value) || 100 })}
                    placeholder="Max points" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveNewAssignment}
                    className="bg-gradient-to-r from-violet-500 to-fuchsia-600"
                    data-testid="add-assignment-submit">
                    <Plus className="w-4 h-4 mr-1" /> Add Assignment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    );
  };

  const AssignmentRow = ({ a, isEditing, onStartEdit, onCancelEdit, onSave, onDelete }) => {
    const [t, setT] = useState(a.title);
    const [d, setD] = useState(a.description);
    const [due, setDue] = useState(a.due_date || '');
    const [pts, setPts] = useState(a.max_points || 100);

    if (isEditing) {
      return (
        <div className="rounded-xl border border-violet-500/40 bg-violet-500/5 p-4 space-y-2" data-testid={`edit-assign-${a.assignment_id}`}>
          <Input value={t} onChange={e => setT(e.target.value)} />
          <Textarea rows={2} value={d} onChange={e => setD(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={due} onChange={e => setDue(e.target.value)} />
            <Input type="number" value={pts} onChange={e => setPts(parseInt(e.target.value) || 100)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancel</Button>
            <Button size="sm" onClick={() => onSave({ title: t, description: d, due_date: due, max_points: pts })}>Save</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-border p-4 flex items-start gap-3 hover:border-violet-500/30 transition" data-testid={`assignment-${a.assignment_id}`}>
        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
          <ClipboardList className="w-4 h-4 text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{a.title}</div>
          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</div>
          <div className="flex gap-2 mt-2 text-[11px] text-muted-foreground">
            <span>{a.max_points} pts</span>
            {a.due_date && <span>· Due {a.due_date}</span>}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="icon" variant="ghost" onClick={onStartEdit}><FileText className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={onDelete}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
        </div>
      </div>
    );
  };

  const Section = ({ icon: Icon, title, subtitle, right, children }) => (
    <div className="mb-8 pb-8 border-b border-border/40 last:border-0">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <div className="text-sm font-semibold">{title}</div>
            {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );

  // ===== DASHBOARD HOME =====
  const DashboardHome = () => (
    <div className="max-w-6xl mx-auto px-8 py-8 space-y-6" data-testid="dashboard-home">
      <header>
        <h2 className="text-2xl font-bold">Welcome back, {(user?.name || '').split(' ')[0]} 👋</h2>
        <p className="text-sm text-muted-foreground mt-1">Click any topic in the curriculum tree on the left to start adding content.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Courses" value={stats.courses} icon={Library} tint="emerald" />
        <StatCard label="Topics" value={stats.topics} subtitle={`${stats.videos} with video`} icon={PlayCircle} tint="sky" />
        <StatCard label="Assignments" value={stats.assignments} icon={ClipboardList} tint="violet" />
        <StatCard label="Empty Topics" value={stats.emptyTopics} subtitle="Need content" icon={AlertCircle} tint="rose" />
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3">How it works</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">1</div>
              <span>Expand a course in the sidebar, then drill down through its <strong>folders</strong> until you reach a <strong>leaf topic</strong>.</span>
            </li>
            <li className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">2</div>
              <span>Click any <strong className="text-sky-400">Topic (leaf node with the <FileText className="w-3 h-3 inline" /> icon)</strong> to open the right-pane editor.</span>
            </li>
            <li className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">3</div>
              <span>Paste a <strong>YouTube/Vimeo URL</strong> — it previews instantly. Add optional reading material in markdown.</span>
            </li>
            <li className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">4</div>
              <span>Create <strong>assignments</strong> inline at the bottom of each topic — they stay tied to the topic.</span>
            </li>
            <li className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center shrink-0">5</div>
              <span>Hover over a <strong className="text-amber-400">Chapter</strong> (<Folder className="w-3 h-3 inline" /> folder) and click the <Plus className="w-3 h-3 inline" /> to add a new topic or sub-chapter.</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );

  const StatCard = ({ label, value, subtitle, icon: Icon, tint }) => {
    const tints = {
      emerald: 'from-emerald-500/20 to-teal-500/10 text-emerald-400 border-emerald-500/20',
      sky:     'from-sky-500/20 to-blue-500/10 text-sky-400 border-sky-500/20',
      violet:  'from-violet-500/20 to-fuchsia-500/10 text-violet-400 border-violet-500/20',
      rose:    'from-rose-500/20 to-orange-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <Card className={`bg-gradient-to-br ${tints[tint]} border`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
              <div className="text-3xl font-bold mt-1">{value}</div>
              {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
            </div>
            <Icon className="w-5 h-5 opacity-70" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const ProfileView = () => (
    <div className="max-w-3xl mx-auto px-8 py-8 space-y-5" data-testid="profile-view">
      <h2 className="text-2xl font-bold">Your Profile</h2>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-4 pb-4 border-b border-border">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xl font-bold">
              {(user?.name || '?').charAt(0)}
            </div>
            <div>
              <div className="text-lg font-semibold">{user?.name}</div>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
              <Badge variant="outline" className="mt-1">Content Executive</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <div className="flex-1 min-w-0">
        {activeTopic ? <TopicEditor />
          : view === 'profile' ? <ProfileView />
          : <DashboardHome />}
      </div>
    </div>
  );
};

export default ContentDashboard;
